import Money from 'js-money';
import moment from 'moment';
import {PaymentType} from "../components/constants/securspace-constants";

const CURRENCY_US_DOLLAR = 'usd';

interface PaymentUtilsType {
  CURRENCY_US_DOLLAR: string;
  convertSmallestSubUnitToMainUnit(amountInCurrencySmallestSubUnit: number, currency: string): number;
}

const PaymentUtils: PaymentUtilsType = {
  CURRENCY_US_DOLLAR,

  convertSmallestSubUnitToMainUnit(amountInCurrencySmallestSubUnit: number, currency: string): number {
    if (!amountInCurrencySmallestSubUnit) {
      return amountInCurrencySmallestSubUnit;
    }
    if (CURRENCY_US_DOLLAR === currency) {
      return amountInCurrencySmallestSubUnit / 100;
    } else {
      throw new Error("Currency is not supported:  " + currency);
    }
  }
};

function isInteger(x: string | number): boolean {
  return !!x && (Number.isInteger(x as number) || ((x as string).indexOf('.') < 0 && (x as number) % 1 === 0));
}

function formatCurrencyValue(value: string | number, showZero?: boolean, setToZero?: boolean): string | number {
  if (value) {
    value = parseCurrencyValue(value);
    if (isInteger(value)) {
      const valueMoney = new Money(parseInt(value.toString()), Money.USD);
      const moneyString = valueMoney.toString();
      const moneyParts = moneyString.split(".");
      value = Money.USD.symbol + moneyParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + moneyParts[1];
    }
  } else if (showZero && setToZero) {
    return 0;
  }
  if (value === 0 || value === "$0" || value === "$0.0" || value === "$0.00") {
    value = showZero ? "$0" : "";
  }
  // https://envase.monday.com/boards/3334982114/views/81030767/pulses/3646888010
  // commented out due to having -$100 which is going to have a single quote added
  // like: '-$100 when sanitized for CSV injection
  // if (value && value.indexOf('-') > 0) {
  //     value = "-" + value.replace("-", "");
  // }
  return value;
}

function validateCurrencyValue(value: string | number): boolean {
  if (!value) {
    return true;
  }

  value = removeFormatting(value);

  return Number.isInteger(value);
}

function removeFormatting(value: string | number): string | number {
  if (!isInteger(value)) {
    value = removeFormatCharacters(value as string);
  }
  if (isInteger(value)) {
    //Removes any leading zeros and ensures value is an Integer
    value = parseInt(value.toString());
  }
  return value;
}

/**
 * Parses a currency value to an integer. If given a string, removes any formatting characters and parses it to an integer.
 * @example "$100" would be parsed to 100 and "$100.55" would be parsed to 10055.
 * @param value {string | number | null | undefined}
 * @returns {number|*}
 */
function parseCurrencyValue(value: string | number | null | undefined): number {
  if (value !== null && value !== undefined) {
    if (typeof value === 'string') {
      value = removeFormatCharacters(value);
      if (value.trim() === '') {
        return 0; // Return 0 for empty spaces
      }
    }
    if (!isNaN(value as number)) {
      value = parseInt(value.toString());
    }
  }
  return (value as number) || 0; // Return 0 if value is falsy (e.g., null, undefined)
}

function removeFormatCharacters(value: string): string {
  return value.replace(/[$,.]/g, '');
}

function getPricePerDayFromWeeklyRate(weeklyRate: string | number): Money {
  weeklyRate = parseCurrencyValue(weeklyRate);
  const weeklyRateMoney = new Money(weeklyRate, Money.USD);
  return weeklyRateMoney.divide(7);
}

function getPricePerDayFromMonthlyRate(monthlyRate: string | number): Money {
  monthlyRate = parseCurrencyValue(monthlyRate);
  const monthlyRateMoney = new Money(monthlyRate, Money.USD);
  const averageNumberOfDaysInAMonth = 30.42;
  return monthlyRateMoney.divide(averageNumberOfDaysInAMonth);
}

interface Location {
  pricePerDay?: number;
  pricePerWeek?: number;
  pricePerMonth?: number;
}

const getNormalizedRateForSort = (location: Location): number => {
  if (location.pricePerDay && location.pricePerDay > 0) return location.pricePerDay;
  if (location.pricePerWeek && location.pricePerWeek > 0) return getPricePerDayFromWeeklyRate(location.pricePerWeek).getAmount();
  if (location.pricePerMonth) return getPricePerDayFromMonthlyRate(location.pricePerMonth).getAmount();
  return 0;
}

function formatMoneyAddDecimalPoint(amount: number): string {
  // Convert the amount to a floating-point number
  return (amount / 100).toFixed(2);
}

function formatMoneyAddDecimalPointAndThousands(
  amount: number,
  currency: string = "USD",
  decimalDelimiter: string = ".",
  thousandsDelimiter: string = ","
): string {
  // Convert the amount to a floating-point number and format it with thousands separator (amount / 100).toFixed(2)
  const currencyObject = (Money as any)[currency];
  amount = amount ? amount : 0;
  const valueMoney = new Money(parseInt(amount.toString()), currencyObject);
  const moneyString = valueMoney.toString();
  const moneyParts = moneyString.split(".");

  return currencyObject.symbol + moneyParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsDelimiter) + decimalDelimiter + moneyParts[1];
}

interface PaymentMethod {
  type?: string;
  cardBrand?: string;
}

function determinePaymentImage(paymentMethod: PaymentMethod): string {
  if (paymentMethod?.type === 'ACH' || paymentMethod?.type === 'BANK') {
    return "https://static.secur.space/icons/Bank.png";
  } else {
    switch (paymentMethod?.cardBrand?.toLowerCase()) {
      case 'visa':
        return "https://static.secur.space/icons/Visa.png";
      case 'mastercard':
        return "https://static.secur.space/icons/MasterCard.png";
      case 'amex':
      case 'american express':
        return "https://static.secur.space/icons/AmericanExpress.png";
      case 'discover':
        return "https://static.secur.space/icons/Discover.png";
      case 'diners':
      case 'diners club':
        return "https://static.secur.space/icons/DinersClub.png";
      case 'unionpay':
        return "https://static.secur.space/icons/UnionPay.png";
      case 'jcb':
        return "https://static.secur.space/icons/JCB.png";
      default:
        return "https://static.secur.space/icons/Visa.png";
    }
  }
}

export interface DwollaError {
  message: string;
}

export interface DwollaErrorResponse {
  status?: number;
  code?: string;
  message?: string;
  _embedded?: {
    errors: DwollaError[];
  };
  // DuplicateResource points at the funding source that already exists
  _links?: {
    about?: { href?: string };
  };
}

const parseDwollaAPIError = (errorResponse: DwollaErrorResponse, statusCode?: number): string[] => {
  try {
    const responseStatus = statusCode || errorResponse.status || 400;
    if (Number.isInteger(responseStatus) && 500 > responseStatus && responseStatus >= 400) {
      if (errorResponse.code === "ValidationError") {
        if (errorResponse._embedded && errorResponse._embedded.errors.length > 1) {
          return errorResponse._embedded.errors.map((error: DwollaError) => error.message);
        }
        return [errorResponse._embedded!.errors[0].message];
      }
      return [errorResponse.message || "Error occurred"];
    }
    return ["Internal Server Error"];
  } catch {
    return ["Internal Server Error"];
  }
};

/**
 * Determines if a payment method is expired.
 * Cards are valid through the end of their expiration month.
 * e.g., if today is 12/2025 and the card expires on 12/2025, the card is still valid.
 * Only cards with expiration dates BEFORE the current month are expired.
 * Non-card payment methods are never expired.
 * @param method Payment method object with type and expiresOn properties
 * @returns {boolean} true if the payment method is expired, false otherwise
 */
const isPaymentMethodExpired = (method: { type?: string; expiresOn?: string }): boolean => {
  const EXPIRATION_DATE_FORMAT = 'M/YYYY';

  // Non-card payment methods are never expired
  if (method.type !== PaymentType.CARD) {
    return false;
  }

  // Validate expiration date format
  const expirationDate = moment(method.expiresOn, EXPIRATION_DATE_FORMAT);
  if (!expirationDate.isValid()) {
    return true;
  }

  // Check if expiration date is before the current month
  const thisMonth = moment().startOf('month');
  return expirationDate.isBefore(thisMonth, 'month');
};

export {
  PaymentUtils as default,
  getPricePerDayFromWeeklyRate,
  getPricePerDayFromMonthlyRate,
  formatCurrencyValue,
  parseCurrencyValue,
  validateCurrencyValue,
  formatMoneyAddDecimalPoint,
  formatMoneyAddDecimalPointAndThousands,
  determinePaymentImage,
  parseDwollaAPIError,
  isInteger,
  getNormalizedRateForSort,
  isPaymentMethodExpired
};
